import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight, Users, Zap, Heart, Rocket, Crown, Star } from 'lucide-react';
import { membership } from '../data/plansData';

const FinalCTA = () => {
  return (
    <section 
      id="final-cta"
      className="relative py-24 md:py-40 overflow-hidden"
      data-testid="final-cta"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-surface-dark">
        <motion.div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% 50%, rgba(0, 255, 0, 0.15) 0%, transparent 60%)`,
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 0, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 0, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
          animate={{
            backgroundPosition: ['0px 0px', '60px 60px'],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />

        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 4 + 1,
              height: Math.random() * 4 + 1,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? '#00FF00' : '#FFD700',
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0.1, 0.6, 0.1],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-green/40 bg-neon-green/10 mb-8"
          whileHover={{ scale: 1.05 }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Heart className="w-4 h-4 text-neon-green" />
          </motion.div>
          <span className="text-neon-green text-sm font-semibold">
            Junte-se a milhares de pessoas reais
          </span>
        </motion.div>

        <motion.h2 
          className="font-outfit font-black text-4xl sm:text-5xl lg:text-7xl text-white mb-6 tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Se você quer{' '}
          <span className="gradient-text-green">construir junto</span>,
          <br />
          <span className="gradient-text-gold">é aqui</span>.
        </motion.h2>

        <motion.p 
          className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-12 leading-relaxed"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          Você não entra apenas para consumir conteúdo. Você entra para{' '}
          <span className="text-neon-green font-semibold">participar, colaborar e evoluir</span> junto com quem está moldando o futuro da IA no país.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
        >
          {/* Main CTA - Member */}
          <motion.a
            href={membership.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-10 py-5 bg-neon-green text-black font-black rounded-full text-lg relative overflow-hidden group shadow-[0_0_40px_rgba(0,255,0,0.3)]"
            whileHover={{ scale: 1.05, boxShadow: '0 0 60px rgba(0, 255, 0, 0.6)' }}
            whileTap={{ scale: 0.95 }}
            data-testid="final-cta-membership"
          >
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-neon-green"
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <Crown className="w-5 h-5 relative z-10" />
            <span className="relative z-10">Quero Ser Membro</span>
            <ChevronRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
          </motion.a>

          {/* Free CTA - muted */}
          <a
            href={membership.freeCtaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 text-neutral-500 hover:text-neutral-300 transition-colors text-sm underline underline-offset-4"
            data-testid="final-cta-free"
          >
            Ou apenas explorar gratuitamente
          </a>
        </motion.div>

        {/* Final message */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 pt-8 border-t border-white/10"
        >
          <p className="text-white/60 text-sm max-w-2xl mx-auto italic">
            "Reunimos quem aprende, quem constrói, quem contrata e quem gera oportunidades
            em um único ambiente vivo, onde relacionamento não é superficial:{' '}
            <span className="text-neon-green not-italic font-semibold">é ativo real e estratégico</span>."
          </p>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-surface-dark via-surface-dark/50 to-transparent" />
    </section>
  );
};

export default FinalCTA;
